import React, { useState, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { FileUpload } from 'primereact/fileupload';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import { fileDownloadService } from '../../apiSetup/services/FileDownloadService';

interface CsvStudent {
  name: string;
  gender: string;
  class: string;
  section?: string;
  email_id: string;
  admission_no: string;
  roll_no: string;
  dob: string;
  blood_group: string;
  father_name: string;
  mother_name: string;
  father_no: string;
  mother_no: string;
  address: string;
  user_login: string;
  user_pass: string;
}

interface CsvPreviewGroup {
  class_name: string;
  section?: string;
  students: CsvStudent[];
}

// Matches actual API error shape: { message: string, details: string }
interface ProcessError {
  message: string;
  details: string;
}

interface ProcessResults {
  success: number;
  failed: number;
  errors?: ProcessError[];
  successful_students: Array<{
    name: string;
    admission_no: string;
    student_id: number;
    user_id: number;
  }>;
}

interface ApiResponse {
  status: 'success' | 'error';
  message: string;
  process_results: ProcessResults;
}

export default function StudentBulkUpload() {
  const [uploading, setUploading] = useState(false);
  const [csvPreview, setCsvPreview] = useState<CsvPreviewGroup[]>([]);
  const [fileSelected, setFileSelected] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ProcessError[]>([]);
  const [successResults, setSuccessResults] = useState<ApiResponse | null>(null);
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);

  const styles = `
    @keyframes fadeInSlide {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .p-fileupload .p-button {
      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
      border: none !important;
      border-radius: 12px !important;
      padding: 10px 20px !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3) !important;
    }
    .p-fileupload .p-button:hover {
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4) !important;
    }
    .p-fileupload-content {
      background: #f9fafb !important;
      border: 2px dashed #e5e7eb !important;
      border-radius: 12px !important;
      padding: 20px !important;
    }
    .error-row {
      background: #fef2f2 !important;
      border-left: 4px solid #ef4444 !important;
    }
  `;

  // Generates: firstnamelastname1234  (no underscore, all lowercase)
  const generateUserLogin = (fullName: string): string => {
    const parts = fullName.trim().toLowerCase().replace(/[^a-z\s]/g, '').split(/\s+/);
    const firstName = parts[0] || 'user';
    const lastName = parts[parts.length - 1] || '';
    const digits = Math.floor(1000 + Math.random() * 9000).toString();
    return lastName && lastName !== firstName
      ? `${firstName}${lastName}${digits}`
      : `${firstName}${digits}`;
  };

  // Generates: firstnamelastname1234@skoolpillar.com
  const generateEmail = (fullName: string): string => {
    const login = generateUserLogin(fullName);
    return `${login}@skoolpillar.com`;
  };

  // Only auto-generate when the value is literally "DEFAULT" (case-insensitive)
  const isDefault = (val: string) => val.trim().toUpperCase() === 'DEFAULT';

  const handleFileSelect = (event: any) => {
    const file = event.files[0];
    if (!file) {
      setFileSelected(false);
      return;
    }

    setFileSelected(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      parseCsvPreview(text);
    };
    reader.readAsText(file);
  };

  const parseCsvLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const parseCsvPreview = (text: string) => {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l);
    if (lines.length < 2) {
      toast.current?.show({
        severity: 'error',
        summary: 'Invalid CSV',
        detail: 'CSV must have header and at least one data row',
        life: 3000,
      });
      return;
    }

    const headers = parseCsvLine(lines[0]).map(h => h.toLowerCase().trim());

    const columnMap: { [key: string]: string } = {
      'father_nam': 'father_name',
      'mother_nam': 'mother_name',
      'admission_no': 'admission_no',
      'nt_roll_no': 'roll_no',
      'admission.': 'admission_no',
      'nt_roll_': 'roll_no',
    };

    const normalizedHeaders = headers.map(h => {
      let cleaned = h.replace(/[._]+$/, '');
      return columnMap[cleaned] || cleaned;
    });

    const required = ['name','gender','class','email_id','admission_no','roll_no','dob','blood_group','father_name','mother_name','father_no','mother_no','address','user_login','user_pass'];
    const missing = required.filter(h => !normalizedHeaders.includes(h));

    if (missing.length > 0) {
      toast.current?.show({
        severity: 'error',
        summary: 'Missing Columns',
        detail: `Required: ${missing.join(', ')}. Found: ${normalizedHeaders.join(', ')}`,
        life: 6000,
      });
      return;
    }

    const classMap: { [key: string]: CsvPreviewGroup } = {};
    let successfulRows = 0;
    let skippedRows = 0;

    for (let i = 1; i < lines.length; i++) {
      const values = parseCsvLine(lines[i]).map(v => v.replace(/^"|"$/g, ''));

      if (values.length < normalizedHeaders.length) {
        skippedRows++;
        continue;
      }

      const row: any = {};
      normalizedHeaders.forEach((h, idx) => {
        row[h] = values[idx] || '';
      });

      if (!row.name || !row.class || !row.admission_no) {
        skippedRows++;
        continue;
      }

      const key = `${row.class}${row.section ? '-' + row.section : ''}`;
      if (!classMap[key]) {
        classMap[key] = {
          class_name: row.class,
          section: row.section || undefined,
          students: [],
        };
      }

      // Normalize DOB: convert DD/MM/YYYY → DD-MM-YYYY for backend compatibility
      const rawDob = row.dob || '';
      const normalizedDob = rawDob.replace(/\//g, '-');

      classMap[key].students.push({
        name: row.name || '',
        gender: row.gender || '',
        class: row.class || '',
        section: row.section || undefined,
        email_id: isDefault(row.email_id) ? generateEmail(row.name) : row.email_id,
        admission_no: row.admission_no || '',
        roll_no: row.roll_no || '',
        dob: normalizedDob,
        blood_group: row.blood_group || '',
        father_name: row.father_name || '',
        mother_name: row.mother_name || '',
        father_no: row.father_no || '',
        mother_no: row.mother_no || '',
        address: row.address || '',
        user_login: isDefault(row.user_login) ? generateUserLogin(row.name) : row.user_login,
        user_pass: row.user_pass || 'DEFAULT',
      });

      successfulRows++;
    }

    const preview = Object.values(classMap);
    setCsvPreview(preview);

    const total = preview.reduce((sum, g) => sum + g.students.length, 0);

    if (skippedRows > 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Preview Ready with Warnings',
        detail: `${preview.length} class(es), ${total} student(s) parsed. ${skippedRows} row(s) skipped.`,
        life: 5000,
      });
    } else {
      toast.current?.show({
        severity: 'info',
        summary: 'Preview Ready',
        detail: `${preview.length} class(es), ${total} student(s)`,
        life: 3000,
      });
    }
  };

  const handleFileUpload = async (event: any) => {
    const file = event.files[0];
    if (!file) return;

    // Rebuild CSV from the already-parsed preview (which has generated logins/emails)
    // so the backend receives enriched data instead of the raw DEFAULT values.
    // IMPORTANT: column order must exactly match what the backend expects.
    // Use bracket notation s['class'] to avoid JS reserved word issues with dot notation.
    let uploadFile = file;
    if (csvPreview.length > 0) {
      const headers = [
        'admission_no', 'roll_no', 'name', 'gender', 'father_name', 'mother_name',
        'address', 'father_no', 'mother_no', 'dob', 'class', 'email_id', 'section',
        'blood_group', 'user_login', 'user_pass',
      ];
      const rows = csvPreview.flatMap(group =>
        group.students.map(s => {
          const values = [
            s.admission_no,
            s.roll_no,
            s.name,
            s.gender,
            s.father_name,
            s.mother_name,
            s.address,
            s.father_no,
            s.mother_no,
            s.dob,
            s['class'],      // bracket notation — avoids any TS reserved-word quirks
            s.email_id,
            s.section ?? '',
            s.blood_group,
            s.user_login,
            s.user_pass,
          ];
          return values.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',');
        })
      );
      const csvString = [headers.join(','), ...rows].join('\n');
      console.log('Rebuilt CSV preview (first 3 rows):\n', csvString.split('\n').slice(0, 4).join('\n'));
      const blob = new Blob([csvString], { type: 'text/csv' });
      uploadFile = new File([blob], 'students_processed.csv', { type: 'text/csv' });
    }

    const formData = new FormData();
    formData.append('csv_file', uploadFile);

    try {
      setUploading(true);
      const response = await apiClient.post<ApiResponse>('/register-students-csv', formData);
      const result = response.data;

      console.log('API Response:', result);

      if (result.status === 'error') {
        const errors = result.process_results?.errors || [];
        setValidationErrors(errors);
        setShowErrors(errors.length > 0);

        // Show partial success panel if some students went through
        if (result.process_results?.success > 0) {
          setSuccessResults(result);
        } else {
          setSuccessResults(null);
        }

        toast.current?.show({
          severity: 'error',
          summary: 'Processing Error',
          detail: result.message,
          life: 5000,
        });
      } else {
        setSuccessResults(result);
        setValidationErrors([]);
        toast.current?.show({
          severity: 'success',
          summary: 'Upload Complete',
          detail: `${result.process_results.success} students registered!`,
          life: 4000,
        });

        setCsvPreview([]);
        setFileSelected(false);
        if (fileUploadRef.current) fileUploadRef.current.clear();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Upload Failed',
        detail: error.response?.data?.message || 'Server error',
        life: 3000,
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleCSV = async () => {
    await fileDownloadService.downloadFile({
      filename: 'students.csv',
      onSuccess: (filename) => {
        toast.current?.show({
          severity: 'success',
          summary: 'Download Complete',
          detail: `${filename} downloaded successfully`,
          life: 3000,
        });
      },
      onError: (error) => {
        toast.current?.show({
          severity: 'error',
          summary: 'Download Failed',
          detail: error.response?.data?.error || 'Failed to download file',
          life: 3000,
        });
      },
      onProgress: (progress) => {
        console.log(`Download progress: ${progress}%`);
      },
    });
  };

  const totalStudents = csvPreview.reduce((sum, g) => sum + g.students.length, 0);
  const isPartialSuccess = successResults?.status === 'error' && (successResults?.process_results?.success ?? 0) > 0;

  return (
    <>
      <style>{styles}</style>
      <Toast ref={toast} />

      <div style={{ minHeight: '100vh', padding: '0px', background: '#fdfdfdff' }}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)',
          borderRadius: '24px',
          padding: '32px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '16px',
              padding: '12px',
            }}>
              <i className="pi pi-users" style={{ fontSize: '18px', color: 'white' }}></i>
            </div>
            <div>
              <h1 style={{ color: 'white', fontSize: '28px', fontWeight: 600, margin: '0 0 4px 0' }}>
                Student Bulk Registration
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  background: 'rgba(74,222,128,0.9)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600,
                }}>
                  {totalStudents} Students
                </span>
                <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
                  {new Date().toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Card */}
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '28px',
          marginBottom: '24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              borderRadius: '16px',
              padding: '12px',
            }}>
              <i className="pi pi-upload" style={{ fontSize: '18px', color: 'white' }}></i>
            </div>
            <div>
              <h3 style={{ color: '#2855ae', fontSize: '18px', fontWeight: 600, margin: '0 0 4px 0' }}>
                Upload Student CSV
              </h3>
              <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>
                Register multiple students at once using CSV file
              </p>
            </div>
          </div>

          <FileUpload
            ref={fileUploadRef}
            name="csv_file"
            accept=".csv"
            maxFileSize={10000000}
            customUpload
            uploadHandler={handleFileUpload}
            onSelect={handleFileSelect}
            onClear={() => { setFileSelected(false); setCsvPreview([]); }}
            onRemove={() => { setFileSelected(false); setCsvPreview([]); }}
            disabled={uploading}
            chooseLabel="Choose CSV"
            uploadLabel="Register Students"
            cancelLabel="Clear"
            emptyTemplate={<p style={{ color: '#6b7280', textAlign: 'center', margin: 0 }}>Drag and drop CSV file here</p>}
          />

          {uploading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '20px' }}>
              <ProgressSpinner style={{ width: '40px', height: '40px' }} />
              <span style={{ marginLeft: '12px', color: '#2855ae', fontWeight: 500 }}>Processing students...</span>
            </div>
          )}
        </div>

        {/* Instructions */}
        {csvPreview.length === 0 && !successResults && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            border: '2px solid #dbeafe',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                borderRadius: '10px',
                padding: '8px',
                minWidth: '32px',
                height: '32px',
              }}>
                <i className="pi pi-info-circle" style={{ fontSize: '14px', color: 'white' }}></i>
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{ color: '#2855ae', fontSize: '14px', fontWeight: 600, margin: '0 0 8px 0' }}>
                  CSV Format Requirements
                </h4>
                <div style={{ color: '#6b7280', fontSize: '13px', lineHeight: '1.6' }}>
                  <p><strong>Required columns:</strong> {['name','gender','class','email_id','admission_no','roll_no','dob','blood_group','father_name','mother_name','father_no','mother_no','address','user_login','user_pass'].join(', ')}</p>
                  <p><strong>Optional:</strong> section</p>
                  <p><strong>DOB format:</strong> DD-MM-YYYY</p>
                  <p><strong>DEFAULT values:</strong> Use "DEFAULT" for auto-generated username/password/email</p>
                </div>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <Button
                onClick={downloadSampleCSV}
                icon="pi pi-download"
                label="Download Sample CSV"
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '10px 20px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              />
            </div>
          </div>
        )}

        {/* CSV Preview */}
        {csvPreview.length > 0 && (
          <div style={{
            background: '#f9fafb',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #e5e7eb',
            marginBottom: '24px',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px',
            }}>
              <h4 style={{
                color: '#2855ae',
                fontSize: '16px',
                fontWeight: 600,
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <i className="pi pi-eye"></i>
                CSV Preview ({csvPreview.length} class(es), {totalStudents} students)
              </h4>
              <Button
                icon="pi pi-times"
                label="Clear"
                onClick={() => {
                  setCsvPreview([]);
                  setFileSelected(false);
                  fileUploadRef.current?.clear();
                }}
                style={{
                  background: '#ef4444',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  fontSize: '12px',
                }}
              />
            </div>

            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {csvPreview.map((group, gIdx) => (
                <div key={gIdx} style={{ marginBottom: '20px' }}>
                  <h5 style={{
                    color: '#2855ae',
                    fontSize: '14px',
                    fontWeight: 600,
                    margin: '0 0 8px 0',
                    paddingBottom: '4px',
                    borderBottom: '1px solid #e5e7eb',
                  }}>
                    {group.class_name}{group.section && ` - Section ${group.section}`} ({group.students.length} students)
                  </h5>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {group.students.map((student, sIdx) => (
                      <div key={sIdx} style={{
                        background: 'white',
                        borderRadius: '8px',
                        padding: '12px',
                        border: '1px solid #e5e7eb',
                        fontSize: '13px',
                      }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                          <div><strong>Name:</strong> {student.name}</div>
                          <div><strong>Roll:</strong> {student.roll_no}</div>
                          <div><strong>DOB:</strong> {student.dob}</div>
                          <div><strong>Email:</strong> {student.email_id}</div>
                          <div><strong>Username:</strong> {student.user_login}</div>
                          <div><strong>Password:</strong> {student.user_pass === 'DEFAULT' ? 'Auto' : '••••••••'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Success / Partial Success Results */}
        {successResults && (
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '28px',
            marginTop: '24px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            animation: 'fadeInSlide 0.5s ease-out',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
              <div style={{
                background: isPartialSuccess
                  ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '16px',
                padding: '12px',
              }}>
                <i
                  className={isPartialSuccess ? 'pi pi-exclamation-triangle' : 'pi pi-check-circle'}
                  style={{ fontSize: '18px', color: 'white' }}
                ></i>
              </div>
              <div>
                <h3 style={{ color: '#2855ae', fontSize: '18px', fontWeight: 600, margin: '0 0 4px 0' }}>
                  {isPartialSuccess ? 'Partial Registration — Transaction Rolled Back' : 'Registration Successful!'}
                </h3>
                <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>
                  {successResults.message}
                </p>
              </div>
              <Button
                icon="pi pi-times"
                onClick={() => { setSuccessResults(null); setValidationErrors([]); }}
                style={{
                  marginLeft: 'auto',
                  background: '#ef4444',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px',
                  color: 'white',
                }}
              />
            </div>

            {/* Stats Row */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: '16px',
              marginBottom: '20px',
            }}>
              <div style={{
                background: '#f0fdf4',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #bbf7d0',
              }}>
                <div style={{ color: '#6b7280', fontSize: '12px', marginBottom: '4px' }}>Successfully Registered</div>
                <div style={{ color: '#16a34a', fontSize: '28px', fontWeight: 700 }}>
                  {successResults.process_results.success}
                </div>
              </div>

              {(successResults.process_results.failed ?? 0) > 0 && (
                <div style={{
                  background: '#fef2f2',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #fca5a5',
                }}>
                  <div style={{ color: '#6b7280', fontSize: '12px', marginBottom: '4px' }}>Failed</div>
                  <div style={{ color: '#dc2626', fontSize: '28px', fontWeight: 700 }}>
                    {successResults.process_results.failed}
                  </div>
                </div>
              )}
            </div>

            {/* Inline Errors — shown here when there's partial success */}
            {isPartialSuccess && validationErrors.length > 0 && (
              <div style={{
                background: '#fef2f2',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #fca5a5',
                marginBottom: '20px',
              }}>
                <h4 style={{
                  color: '#dc2626',
                  fontSize: '14px',
                  fontWeight: 600,
                  margin: '0 0 12px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <i className="pi pi-exclamation-circle"></i>
                  Processing Errors ({validationErrors.length})
                </h4>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {validationErrors.map((err, idx) => (
                    <div key={idx} style={{
                      background: 'white',
                      borderRadius: '8px',
                      padding: '12px',
                      border: '1px solid #fca5a5',
                      borderLeft: '4px solid #ef4444',
                    }}>
                      <div style={{ fontWeight: 600, color: '#dc2626', fontSize: '13px', marginBottom: '4px' }}>
                        {err.message}
                      </div>
                      {err.details && (
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {err.details}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Successful Students List */}
            {successResults.process_results.successful_students?.length > 0 && (
              <div style={{
                background: '#f9fafb',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #e5e7eb',
                maxHeight: '400px',
                overflowY: 'auto',
              }}>
                <h4 style={{ color: '#2855ae', fontSize: '14px', fontWeight: 600, margin: '0 0 12px 0' }}>
                  Registered Students ({successResults.process_results.successful_students.length})
                </h4>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {successResults.process_results.successful_students.map((student, idx) => (
                    <div key={idx} style={{
                      background: 'white',
                      borderRadius: '8px',
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr 1fr',
                      gap: '12px',
                      fontSize: '13px',
                      color: '#374151',
                    }}>
                      <div>
                        <div style={{ fontWeight: 600, color: '#2855ae' }}>{student.name}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>Adm: {student.admission_no}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>Student ID</div>
                        <div style={{ fontWeight: 600 }}>{student.student_id}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>User ID</div>
                        <div style={{ fontWeight: 600 }}>{student.user_id}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <i className="pi pi-check" style={{ fontSize: '16px', color: '#10b981' }}></i>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Validation Errors Dialog — for pure error (0 successes) */}
        <Dialog
          visible={showErrors && !isPartialSuccess}
          onHide={() => setShowErrors(false)}
          header={`Processing Errors (${validationErrors.length})`}
          style={{ width: '90vw', maxWidth: '800px' }}
          modal
        >
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {validationErrors.map((err, idx) => (
              <div key={idx} style={{
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                borderLeft: '4px solid #ef4444',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '12px',
              }}>
                <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: '4px', fontSize: '13px' }}>
                  {err.message}
                </div>
                {err.details && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    {err.details}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Dialog>

      </div>
    </>
  );
}